// =============================================================================
// Live booking state, owned above the tab switcher.
//
// This deliberately does not live in the Room Tracker screen. An overdue room
// is the whole point of the module, and if the poll and the alarm were mounted
// with the board, switching to Reservations would silence a red room and give
// the operator no idea it had happened. So the poll runs for as long as staff
// are signed in, and the board is just one view onto it.
//
// Re-render cost: the countdown ticks every second, so anything consuming this
// context redraws every second. Consumers are kept to the three things that
// need it — the board, the overdue banner, and the tab badge. `children` is
// passed straight through, so React sees the same element references and skips
// re-rendering the rest of the app on every tick.
// =============================================================================
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react';
import { api, type BookingItem } from './api';
import { alarm } from './alarm';
import { blendOffset, measureOffset, stateOf } from './bookings';

const POLL_MS = 2_000;
const TICK_MS = 1_000;
/** Backoff ceiling: a broken endpoint is retried at most this slowly. */
const MAX_POLL_MS = 30_000;

interface BookingsValue {
  bookings: BookingItem[];
  warningMinutes: number;
  loading: boolean;
  error: string | null;
  /** Wall-clock corrected against the server, recomputed each tick. */
  nowMs: number;
  expiredCount: number;
  refresh: () => Promise<boolean>;
}

const fallback: BookingsValue = {
  bookings: [], warningMinutes: 5, loading: false, error: null,
  nowMs: 0, expiredCount: 0, refresh: async () => true,
};

const BookingsContext = createContext<BookingsValue>(fallback);

export function useBookings(): BookingsValue {
  return useContext(BookingsContext);
}

export function BookingsProvider({ children }: { children: ReactNode }) {
  const [bookings, setBookings] = useState<BookingItem[]>([]);
  const [warningMinutes, setWarningMinutes] = useState(5);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const offsetRef = useRef<number | null>(null);
  const failuresRef = useRef(0);

  const refresh = useCallback(async (): Promise<boolean> => {
    const sentAt = Date.now();
    try {
      const board = await api.bookings();
      const receivedAt = Date.now();
      offsetRef.current = blendOffset(
        offsetRef.current,
        measureOffset(board.serverNow, sentAt, receivedAt),
      );
      setBookings(board.bookings);
      setWarningMinutes(board.warningMinutes);
      setError(null);
      failuresRef.current = 0;
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      failuresRef.current += 1;
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Self-scheduling poll rather than a fixed interval, so a failing endpoint
  // backs off instead of being hammered every two seconds for the rest of the
  // shift — and recovers immediately on the first success.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const run = async () => {
      const ok = await refresh();
      if (cancelled) return;
      const delay = ok
        ? POLL_MS
        : Math.min(POLL_MS * 2 ** failuresRef.current, MAX_POLL_MS);
      timer = setTimeout(run, delay);
    };
    void run();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [refresh]);

  useEffect(() => {
    const id = setInterval(() => setTick(n => n + 1), TICK_MS);
    return () => clearInterval(id);
  }, []);

  // `tick` is the dependency that matters here: it is what makes the clock move
  // between polls.
  const nowMs = useMemo(() => Date.now() + (offsetRef.current ?? 0), [tick, bookings]);

  const expiredCount = useMemo(
    () => bookings.reduce((n, s) => n + (stateOf(s, nowMs, warningMinutes) === 'expired' ? 1 : 0), 0),
    [bookings, nowMs, warningMinutes],
  );

  useEffect(() => { alarm.installGestureUnlock(); }, []);

  // Ring while anything is red, stop when nothing is. arm() is idempotent, so
  // this running on every change of count keeps one cadence rather than one per
  // room.
  useEffect(() => {
    if (expiredCount > 0) alarm.arm();
    else alarm.disarm();
  }, [expiredCount]);

  // Signing out should not leave a siren running.
  useEffect(() => () => alarm.disarm(), []);

  // Flash the tab title so a red room is visible when the browser is behind
  // Opera or another window. Sound is the channel most likely to be blocked, so
  // this has to stand on its own.
  useEffect(() => {
    if (expiredCount === 0) return;
    const original = document.title;
    let on = false;
    const id = setInterval(() => {
      on = !on;
      document.title = on ? `(${expiredCount}) ROOM OVERDUE` : original;
    }, 1_000);
    return () => { clearInterval(id); document.title = original; };
  }, [expiredCount]);

  const value = useMemo<BookingsValue>(
    () => ({ bookings, warningMinutes, loading, error, nowMs, expiredCount, refresh }),
    [bookings, warningMinutes, loading, error, nowMs, expiredCount, refresh],
  );

  return <BookingsContext.Provider value={value}>{children}</BookingsContext.Provider>;
}
